import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { request, getApiBaseUrl } from '../../services/cloudApi';
import { useAuth } from '../../contexts/AuthContext';
import { isModalNotificationActive } from '../../utils/adminNotificationSchedule';

const AdminNotificationModal = ({ notification, onClose }) => {
  const { t } = useTranslation('shared');
  const { authToken } = useAuth();
  const markedSeen = useRef(false);
  const [resolvedImageUrl, setResolvedImageUrl] = React.useState(null);

  React.useEffect(() => {
    if (!notification?.imageUrl) return;
    if (notification.imageUrl.startsWith('http')) {
      setResolvedImageUrl(notification.imageUrl);
      return;
    }
    getApiBaseUrl().then((apiBase) => {
      const serverBase = apiBase.replace('/api', '');
      setResolvedImageUrl(`${serverBase}${notification.imageUrl}`);
    }).catch(() => setResolvedImageUrl(notification.imageUrl));
  }, [notification?.imageUrl]);

  useEffect(() => {
    if (!notification || markedSeen.current) return;
    markedSeen.current = true;
    request(`/admin/notifications/${notification.id}/seen`, {
      method: 'POST',
      token: authToken,
    }).catch(() => {});
  }, [notification, authToken]);

  useEffect(() => {
    if (!notification?.showEndAt) return undefined;
    const end = new Date(notification.showEndAt);
    if (Number.isNaN(end.getTime())) return undefined;

    const tick = () => {
      if (!isModalNotificationActive(notification)) {
        onClose();
      }
    };

    tick();
    const intervalId = setInterval(tick, 30 * 1000);
    const timeoutMs = end.getTime() - Date.now();
    const timeoutId = timeoutMs > 0
      ? setTimeout(onClose, timeoutMs)
      : null;

    return () => {
      clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [notification, onClose]);

  const handleLinkClick = () => {
    request(`/admin/notifications/${notification.id}/clicked`, {
      method: 'POST',
      token: authToken,
    }).catch(() => {});
    if (notification.link) {
      const url = /^https?:\/\//i.test(notification.link)
        ? notification.link
        : `https://${notification.link}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    onClose();
  };

  if (!notification) return null;

  return (
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/75 backdrop-blur-lg z-[99000]"
        onClick={onClose}
      />

      {/* Wrapper */}
      <motion.div
        key="modal"
        initial={{ opacity: 0, scale: 0.88, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.88, y: 24 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        className="fixed inset-0 flex flex-col items-center justify-center z-[99001] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Kapat butonu — modal dışında, tam üstte */}
        <motion.button
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          onClick={onClose}
          className="mb-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.08] hover:bg-white/[0.14] border border-white/[0.10] text-white/60 hover:text-white text-xs font-medium transition-all"
        >
          <X className="w-3.5 h-3.5" />
          {t('adminNotificationModal.close')}
        </motion.button>

        {/* Kart */}
        <div
          className="relative w-full overflow-hidden"
          style={{ maxWidth: 480, aspectRatio: '4/3' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Dış parlak border halkası */}
          <div
            className="absolute inset-0 rounded-[20px] pointer-events-none z-10"
            style={{
              boxShadow: '0 0 0 1.5px rgba(255,255,255,0.13), 0 0 0 4px rgba(61,142,240,0.10), 0 32px 80px rgba(0,0,0,0.75)',
            }}
          />

          {/* İç kart */}
          <div className="relative w-full h-full rounded-[18px] overflow-hidden bg-[#080e1a]">
            {/* Fotoğraf */}
            {resolvedImageUrl ? (
              <img
                src={resolvedImageUrl}
                alt={t('adminNotificationModal.imageAlt')}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0d1424] to-[#1a2640]">
                <p className="text-slate-500 text-sm">{t('adminNotificationModal.noImage')}</p>
              </div>
            )}

            {/* Link butonu — fotoğraf üzerinde alt gradient + buton */}
            {notification.link && (
              <div className="absolute inset-x-0 bottom-0 pt-16 pb-5 px-5 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex flex-col items-center">
                <button
                  onClick={handleLinkClick}
                  className="group relative flex items-center gap-3 px-6 py-3 rounded-2xl text-sm font-semibold text-white overflow-hidden transition-all duration-300 hover:scale-[1.03] active:scale-[0.97]"
                  style={{
                    background: 'linear-gradient(135deg, #3d8ef0 0%, #2563eb 100%)',
                    boxShadow: '0 4px 24px rgba(61,142,240,0.45), inset 0 1px 0 rgba(255,255,255,0.15)',
                  }}
                >
                  {/* Shine sweep */}
                  <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.12) 50%, transparent 60%)' }}
                  />
                  <span className="relative">{t('adminNotificationModal.viewDetails')}</span>
                  <span className="relative flex items-center justify-center w-6 h-6 rounded-full bg-white/20 group-hover:bg-white/30 transition-colors">
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AdminNotificationModal;
